import ms from "ms";
import { inject, injectable } from "inversify";
import { action, computed, makeObservable, observable } from "mobx";
import { modalService } from "@whatslly/ui-components/modals";
import { ArchiveItemModal } from "../ArchiveItemModal";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { ContactDetailsModal } from "../ContactDetailsModal";
import i18n from "../i18n/config";

import type { ImContact } from "@whatslly/types/tuvis-messaging/TuvisMessagingContacts";
import { ALREADY_EXISTS_ERROR } from "@whatslly/types/tuvis-messaging/TuvisMessagingContacts";
import { TuvisMessagingDataStore } from "./TuvisMessagingDataStore";
import { MessagingCommands } from "@whatslly/commons/tuvis-messaging/messagingCommands";
import { AddContactViaCSVModal } from "@whatslly/ui-components/Pages/TuvisMessaging/AddContactViaCSVModal";

@injectable()
export class TuvisContactsStore {
  private _contactsListSearchTerm = "";
  private _selectedContactId: string | undefined;

  constructor(@inject(TuvisMessagingDataStore) private readonly dataStore: TuvisMessagingDataStore) {
    makeObservable<TuvisContactsStore, "_contactsListSearchTerm" | "_selectedContactId">(this, {
      _contactsListSearchTerm: observable,
      _selectedContactId: observable,
      contacts: computed,
      selectedContact: computed,
      filteredContacts: computed,
      removeSelectedContact: action,
      updateSelectedContact: action,
    });
  }

  updateSelectedContact(contact: ImContact) {
    if (this._selectedContactId === contact._id) return;
    this._selectedContactId = contact._id;
  }

  get contactsListSearchTerm() {
    return this._contactsListSearchTerm;
  }

  set contactsListSearchTerm(newSearchTerm) {
    this._contactsListSearchTerm = newSearchTerm;
  }

  get contacts() {
    return this.dataStore.contacts;
  }

  get filteredContacts() {
    const term = this.contactsListSearchTerm.trim().toLowerCase();
    return this.contacts.filter((contact) => !contact.isDeleted && contact.displayName.toLowerCase().includes(term));
  }

  get selectedContact() {
    if (!this._selectedContactId) {
      return;
    }
    return this.contacts.get(this._selectedContactId);
  }

  removeSelectedContact() {
    this._selectedContactId = undefined;
  }

  async addContactsViaCsv() {
    const result = await modalService.openModal(AddContactViaCSVModal).promise;
    if (!result?.contacts) {
      return;
    }
    const res = await this.dataStore.wsClient.send(MessagingCommands.IMPORT_CONTACTS_FROM_CSV, result.contacts);
    if (!res.success) {
      modalService.openModal(Toast, {
        text: i18n.t("whatsapp_business_api_agent_page.failed_to_import_contacts_from_csv"),
        type: "error",
        timeout: ms("30sec"),
      });
      return;
    }
    modalService.openModal(Toast, {
      text: i18n.t("whatsapp_business_api_agent_page.contacts_imported_successfully"),
      timeout: ms("30sec"),
    });
  }

  async addContact(data: { firstName: string; lastName: string; phone: string } = { firstName: "", lastName: "", phone: "" }) {
    const result = await modalService.openModal(ContactDetailsModal, {
      initData: data,
      title: i18n.t("whatsapp_business_api_agent_page.add_contact_modal.add_contact_title"),
    }).promise;
    if (!result) {
      return;
    }
    try {
      const res = await this.dataStore.wsClient.send(MessagingCommands.CREATE_CONTACT, result);
      return this.contacts.get(res._id);
    } catch (e: any) {
      modalService.openModal(Toast, {
        text:
          e.toString() === ALREADY_EXISTS_ERROR
            ? i18n.t("whatsapp_business_api_agent_page.failed_to_add_contact_phone_number_already_exists")
            : i18n.t("whatsapp_business_api_agent_page.failed_to_add_contact"),
        type: "error",
        timeout: ms("30sec"),
      });
    }
  }

  async editContact(contact: ImContact) {
    const result = await modalService.openModal(ContactDetailsModal, {
      initData: {
        firstName: contact.firstName ?? "",
        lastName: contact.lastName ?? "",
        phone: "",
      },
      hidePhone: true,
      title: i18n.t("whatsapp_business_api_agent_page.add_contact_modal.edit_contact_title"),
    }).promise;
    if (!result) {
      return;
    }

    try {
      await this.dataStore.wsClient.send(MessagingCommands.UPDATE_CONTACT, {
        firstName: result.firstName,
        lastName: result.lastName,
        _id: contact._id,
      });
    } catch (e: any) {
      modalService.openModal(Toast, {
        text: i18n.t("whatsapp_business_api_agent_page.failed_to_update_contact"),
        type: "error",
        timeout: ms("30sec"),
      });
    }
  }

  async archiveContact(contact: ImContact) {
    const res = await modalService.openModal(ArchiveItemModal, {
      type: "archive_contact",
      name: `${contact.firstName ?? ""} ${contact.lastName}`.trim(),
    }).promise;
    if (!res?.result) {
      return;
    }

    try {
      await this.dataStore.wsClient.send(MessagingCommands.DELETE_CONTACT, { _id: contact._id });
    } catch (e: any) {
      modalService.openModal(Toast, { text: i18n.t("whatsapp_business_api_agent_page.failed_to_archive_contact"), type: "error" });
    }
  }
}
